import { useEffect, useState } from "react";
import { Bell, BellOff, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { todayIso } from "./labels";

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch {
    return iso || "";
  }
};

// Promemoria attività commerciali: scadute, di oggi e imminenti, collegate alla scheda cliente.
// Le email all'amministratore sono un'opzione configurabile, disattivata finché il cliente non dà il consenso.
export default function RemindersPanel({ onOpenContact }) {
  const [data, setData] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(null);

  const load = () => {
    api
      .get("/reminders")
      .then(({ data }) => setData(data))
      .catch(() => setData({ overdue: [], today: [], upcoming: [] }));
    api
      .get("/settings/reminders")
      .then(({ data }) => setEmailEnabled(!!data.email_reminders_enabled))
      .catch(() => setEmailEnabled(false));
  };

  useEffect(load, []);

  const toggleEmail = async () => {
    const next = !emailEnabled;
    try {
      const { data } = await api.patch("/settings/reminders", { email_reminders_enabled: next });
      setEmailEnabled(!!data.email_reminders_enabled);
      toast.success(next ? "Promemoria via email attivati: riceverai il riepilogo giornaliero alle 8:30" : "Promemoria via email disattivati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (data === null) return null;
  const total = data.overdue.length + data.today.length + data.upcoming.length;
  if (!total) return null;

  const groups = [
    { key: "overdue", title: "SCADUTE", cls: "border-[#e07a5f]/45 text-[#e07a5f]" },
    { key: "today", title: "OGGI", cls: "border-[#d4af37]/55 text-gold-light" },
    { key: "upcoming", title: "IMMINENTI (48 ORE)", cls: "border-[#c9a45c]/30 text-[#c9c6bf]" },
  ];

  return (
    <div className="border border-[#c9a45c]/30 bg-[#101010] p-4" data-testid="reminders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">
          <Bell size={12} className="text-gold" /> Promemoria attività ({total})
        </p>
        <button
          onClick={toggleEmail}
          disabled={emailEnabled === null}
          className={`inline-flex items-center gap-2 border px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${
            emailEnabled ? "border-[#d4af37]/60 bg-[#d4af37]/10 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
          }`}
          data-testid="reminder-email-toggle"
          aria-pressed={emailEnabled === true}
          role="switch"
          aria-label="Promemoria email attività delle 8:30"
        >
          {emailEnabled ? <Bell size={11} /> : <BellOff size={11} />} Email 8:30: {emailEnabled === null ? "…" : emailEnabled ? "ATTIVATI" : "DISATTIVATI"}
        </button>
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 text-[10px] leading-relaxed text-[#8f8f8f]">
        <Info size={10} className="shrink-0 text-gold" />
        Riepilogo giornaliero via email all'amministratore, solo se attivato; nessun invio per attività completate o annullate.
      </p>
      <div className="mt-3 grid gap-3">
        {groups.map(({ key, title, cls }) =>
          (data[key] || []).length ? (
            <div key={key} data-testid={`reminders-${key}`}>
              <p className={`text-[9px] font-bold tracking-[0.14em] ${cls}`}>{title}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {data[key].map((r) => (
                  <button
                    key={r.activity_id}
                    onClick={() => onOpenContact(r.contact_id, r.activity_id)}
                    className={`border bg-[#141414] px-2.5 py-1.5 text-left text-[11px] leading-tight text-[#dedbd4] transition-colors duration-150 hover:border-gold ${cls}`}
                    data-testid={`reminder-chip-${r.activity_id}`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Clock size={10} className="shrink-0 text-gold" />
                      <span className="font-bold">{r.type_label} · {fmtDay(r.due_date)} {r.due_time}</span>
                      <span className="text-[#c9c6bf]">{r.contact_name}</span>
                    </span>
                    {r.description && <span className="mt-0.5 block text-[10px] text-[#9a9791]">{r.description}</span>}
                  </button>
                ))}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}
